import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { PARTNERS } from "@/lib/data";
import { useQuote } from "@/context/QuoteContext";
import { Reveal } from "@/components/Reveal";

const AUTOPLAY = 4200;

export default function Partners() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const { openQuoteModal } = useQuote();

  useEffect(() => {
    const t = setInterval(() => {
      if (!paused) setActive((a) => (a + 1) % PARTNERS.length);
    }, AUTOPLAY);
    return () => clearInterval(t);
  }, [paused, active]);

  const partner = PARTNERS[active];

  return (
    <section id="partners" className="py-24 lg:py-32 bg-medis-subtle/50 border-y border-medis-silver/20" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <Reveal>
              <div className="text-[11.5px] uppercase tracking-[0.22em] text-medis-teal font-semibold">Our Network</div>
              <h2 className="mt-3 font-display font-bold text-medis-navy text-3xl sm:text-4xl lg:text-[40px] leading-[1.1] tracking-tight">
                Pan-India partner ecosystem.
              </h2>
              <p className="mt-4 text-medis-gray text-[15px] leading-relaxed">
                MEDIS operates through a strong network of associated companies, experienced engineers and ready spare
                parts inventory across India.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 flex flex-col gap-2" data-testid="partner-location-list">
            {PARTNERS.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setActive(i)}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                className="flex items-center justify-between rounded-lg border px-4 py-3 text-left transition-all duration-200"
                style={
                  i === active
                    ? { background: "#0b3c82", color: "white", borderColor: "#0b3c82" }
                    : { background: "white", color: "#5f6b7a", borderColor: "#e2e8f0" }
                }
                data-testid={`partner-location-${p.id}`}
              >
                <div className="flex items-center gap-3">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: i === active ? "#00a2d3" : "#a5adb5" }} />
                  <span className="text-[13.5px] font-semibold">{p.location}</span>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.14em] font-bold ${i === active ? "text-white/60" : "text-medis-silver"}`}>
                  {p.region}
                </span>
              </button>
            ))}
            <div className="grid grid-cols-3 gap-2 mt-3">
              <div className="rounded-lg bg-white border border-medis-silver/30 p-3 text-center">
                <div className="font-display font-bold text-medis-blue text-xl">7</div>
                <div className="text-[9.5px] uppercase tracking-[0.1em] text-medis-gray">Partner Companies</div>
              </div>
              <div className="rounded-lg bg-white border border-medis-silver/30 p-3 text-center">
                <div className="font-display font-bold text-medis-blue text-xl">6</div>
                <div className="text-[9.5px] uppercase tracking-[0.1em] text-medis-gray">States Covered</div>
              </div>
              <div className="rounded-lg bg-white border border-medis-silver/30 p-3 text-center">
                <div className="font-display font-bold text-medis-blue text-xl">Pan</div>
                <div className="text-[9.5px] uppercase tracking-[0.1em] text-medis-gray">India Network</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <AnimatePresence mode="wait">
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-2xl bg-white border border-medis-silver/30 p-7 sm:p-9 shadow-sm h-full"
                data-testid="partner-card"
              >
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-medis-teal font-semibold">{partner.region}</div>
                <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-xl bg-medis-blue text-white flex items-center justify-center font-display font-bold text-2xl">
                      {partner.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-display font-bold text-medis-navy text-2xl">{partner.name}</div>
                      <div className="text-[12px] text-medis-gray">{partner.subtitle}</div>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-medis-gray border border-medis-silver/40 rounded-full px-3 py-1.5">
                    <MapPin className="h-3.5 w-3.5 text-medis-teal" /> {partner.location}
                  </div>
                </div>

                <p className="mt-5 text-[14.5px] text-medis-gray leading-relaxed max-w-2xl">{partner.description}</p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {partner.services.map((s) => (
                    <span key={s} className="text-[11px] font-semibold text-medis-blue bg-medis-blue/[0.07] rounded-full px-3 py-1.5">
                      {s}
                    </span>
                  ))}
                </div>

                <div className="mt-6 rounded-xl border border-medis-silver/25 bg-medis-subtle/60 p-5">
                  <div className="text-[10.5px] uppercase tracking-[0.16em] text-medis-gray font-semibold mb-3">Why this partner</div>
                  <ul className="space-y-2.5">
                    {partner.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-[13.5px] text-medis-navy/90">
                        <span className="h-1.5 w-1.5 rounded-full bg-medis-green mt-1.5 shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <div className="font-display font-bold text-medis-blue text-xl">{partner.since}</div>
                      <div className="text-[10px] uppercase tracking-[0.12em] text-medis-gray">Est. Since</div>
                    </div>
                    <div>
                      <div className="font-display font-bold text-medis-blue text-xl">{partner.engineers}</div>
                      <div className="text-[10px] uppercase tracking-[0.12em] text-medis-gray">Engineers</div>
                    </div>
                    <div>
                      <div className="font-display font-bold text-medis-blue text-xl">{partner.coverage}</div>
                      <div className="text-[10px] uppercase tracking-[0.12em] text-medis-gray">Coverage</div>
                    </div>
                  </div>
                  <button
                    onClick={() => openQuoteModal()}
                    className="group inline-flex items-center gap-2 bg-medis-blue text-white px-5 py-3 rounded-md text-[13px] font-semibold hover:bg-medis-blue-dark transition-colors"
                    data-testid="partner-enquire-button"
                  >
                    Enquire About Partnership
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
